import { MoreOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, type MenuProps } from 'antd';
import { t } from 'i18next';
import React, { type JSX } from 'react';

import { ExportButton } from '@client/components/buttons/ExportButton';
import { EAbilityActions } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useLoading } from '@client/store/loading.store';

import styles from './components.module.less';

import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { ItemType } from 'antd/es/menu/interface';

type TToolbarProps<T> = {
  entities?: T;
  children?: React.ReactNode;
  refTarget?: React.RefObject<HTMLDivElement | null>;
  items?: ItemType[];
  onRefresh?: () => void;
  size?: SizeType;
  subject: string;
};

export const Toolbar = <T extends object>(
  props: TToolbarProps<T>,
): JSX.Element => {
  const { loading } = useLoading();
  const { ability } = useAuth();

  const {
    entities,
    children,
    items = [],
    refTarget,
    subject,
    size = 'middle',
    onRefresh,
  } = props;

  let baseItems: MenuProps['items'] = onRefresh
    ? [
        {
          key: 'refresh',
          label: t('actions.refresh'),
          icon: <ReloadOutlined />,
          onClick: onRefresh,
        },
      ]
    : [];

  baseItems.push(
    ExportButton<T>({
      refTarget,
      subject,
      preview: true,
      json: entities as T,
      disabled: ability?.cannot(EAbilityActions.EXPORT, subject),
    }) as unknown as ItemType,
  );

  if (items?.length) {
    baseItems = items.concat([{ type: 'divider' }, ...baseItems]);
  }

  return (
    <div className={styles.toolbar}>
      {children ? children : null}
      <Dropdown
        menu={{ items: baseItems }}
        trigger={['click']}
        classNames={{ root: styles.toolbarMenu }}
      >
        <Button
          loading={loading}
          size={size}
          color="default"
          variant="filled"
          icon={<MoreOutlined />}
          type="primary"
        />
      </Dropdown>
    </div>
  );
};
